// The one card every front page is made of: the home screen's "Pending actions" and
// "Dashboards" tabs and the five section front pages (Master Data, Reports, Governance,
// Grow, Settings). The owner picked this look from a mockup on 22 Oct 2026.
//
// About 96px tall, and the whole card is ONE link:
//   line 1  a small icon square, the page's name on one line (cut with "…"), a faint arrow
//   line 2  ONE number and its one-to-three grey words on the same baseline; at most a
//           second, smaller number after a dot. A card with no number says a grey "Open".
// Nothing else is drawn on the card: no description, no status pill, no time, no
// "Open list" link. What a reader may want beyond that (the page's description, what the
// number means in full, when it was read) is the card's hover title.
//
// Colour says one thing only: an amber number is something to do, above 0. A 0 is a
// light grey number. Every other number is dark. The words are always grey.
//
// While the figures are coming, a small grey bar holds their place; the line keeps one
// height whatever it holds, so nothing jumps when the numbers arrive.
import { Fragment } from 'react';
import type { ComponentType, ReactElement } from 'react';
import { Link, type To } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Skeleton } from '../Skeleton';

/**
 * How a figure's NUMBER is drawn (its words are always grey):
 * - `action`: something waits to be done: amber. Only above 0.
 * - `plain`:  a plain count: dark.
 * - `zero`:   light grey.
 * A numeric 0 is always drawn `zero`, whatever is passed: a 0 never reads as work.
 */
export type CompactFigureTone = 'action' | 'plain' | 'zero';

export interface CompactFigure {
  /** A count (drawn with Indian grouping: 1,24,000) or a short text as given ("Custom", "0/2,410"). */
  value: number | string;
  /** One to three grey words after the number, on its baseline: "waiting", "stores short". */
  label: string;
  /** Omitted: `plain` (a 0 is `zero`). */
  tone?: CompactFigureTone;
}

export interface CompactCardProps {
  /** Where the card goes: the whole card is the link, and nothing inside it is a second one. */
  to: To;
  /** Drawn small in the square at the top left. */
  icon: ComponentType<{ className?: string }>;
  /** The page's name: one line, cut with "…" when it does not fit. */
  name: string;
  /**
   * The card's figures in reading order. The first is drawn large and the second a size
   * down after a dot; any after the second are not drawn but are added to the hover, so
   * nothing the caller sends is lost. None (and not loading): a grey "Open".
   */
  figures?: readonly CompactFigure[];
  /**
   * The hover title: the page's description or blurb, what the figure means in full, and
   * any time ("as of 10:40 am"). Lines may be separated with "\n". Omitted: no title.
   */
  hover?: string;
  /** The figures are still coming: a small grey bar in their place. */
  loading?: boolean;
  /** `action` tints the icon square amber (a "Pending actions" card with something waiting). Default `plain`. */
  iconTone?: 'plain' | 'action';
}

/** The cards' grid on a whole-width page: 1 across on a phone, then 2, 3, and 4 from 1280px. */
export const COMPACT_CARD_GRID = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

/** How many figures a card draws. */
export const COMPACT_CARD_MAX_FIGURES = 2;

/** A figure's value as the card prints it: counts with Indian grouping, text as given. */
export const compactValueText = (value: number | string): string =>
  typeof value === 'number' ? value.toLocaleString('en-IN') : value;

/** The tone a figure's number is drawn in: a numeric 0 is always `zero`. */
export function compactTone(figure: CompactFigure): CompactFigureTone {
  if (typeof figure.value === 'number' && figure.value === 0) return 'zero';
  return figure.tone ?? 'plain';
}

/** Figures as one line of words: "2,455 deployed · 44 undeployed". */
export const compactFiguresText = (figures: readonly CompactFigure[]): string =>
  figures.map((f) => `${compactValueText(f.value)} ${f.label}`).join(' · ');

const TONE: Record<CompactFigureTone, string> = {
  action: 'text-amber-700',
  plain: 'text-gray-900',
  zero: 'text-gray-300',
};

const CARD =
  'group flex h-24 min-w-0 flex-col justify-between rounded-[14px] border border-gray-200 bg-white px-4 py-3.5 ' +
  'transition hover:border-primary-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
const SQUARE = 'flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg';
const SQUARE_TONE = { plain: 'bg-gray-100 text-gray-500', action: 'bg-amber-100 text-amber-700' } as const;
// One height for the second line whatever it holds (figures, the grey bar, "Open").
const FIGURE_LINE = 'flex h-8 min-w-0 items-baseline overflow-hidden whitespace-nowrap';
const WORDS = 'min-w-0 truncate text-[13px] text-gray-500';

/**
 * An invisible character at the large number's size: it gives "Open" and the grey bar
 * the same baseline as the words beside a number, so a row of cards lines up.
 */
const Strut = (): ReactElement => (
  <span aria-hidden="true" className="w-0 text-2xl leading-8">
    {'​'}
  </span>
);

function Figures({ figures }: { figures: readonly CompactFigure[] }): ReactElement {
  return (
    <>
      {figures.map((f, i) => (
        <Fragment key={i}>
          {/* The dot is hidden from a screen reader; the spaces keep the words apart for it
              (on screen a flex row draws no space between its items). */}
          {i > 0 && ' '}
          {i > 0 && (
            <span aria-hidden="true" className="mx-2 shrink-0 text-gray-400">
              ·
            </span>
          )}
          {i > 0 && ' '}
          <span
            data-tone={compactTone(f)}
            className={`shrink-0 font-semibold tabular-nums tracking-tight ${i === 0 ? 'text-2xl leading-8' : 'text-lg'} ${TONE[compactTone(f)]}`}
          >
            {compactValueText(f.value)}
          </span>{' '}
          <span className={`ml-1.5 ${WORDS}`}>{f.label}</span>
        </Fragment>
      ))}
    </>
  );
}

/**
 * The place of a card that is not known yet (its list may still be left out): the card's
 * own frame and height, with a grey square, a grey bar for the name and one for the
 * number. Not a link, and nothing in it is read out.
 */
export function CompactCardPlaceholder(): ReactElement {
  return (
    <div aria-hidden="true" className="flex h-24 min-w-0 flex-col justify-between rounded-[14px] border border-gray-200 bg-white px-4 py-3.5">
      <span className="flex min-w-0 items-center gap-2.5">
        <Skeleton className={`${SQUARE} rounded-lg`} />
        <Skeleton className="h-4 w-32 max-w-full" />
      </span>
      <span className="flex h-8 items-center">
        <Skeleton className="h-5 w-24 max-w-full" />
      </span>
    </div>
  );
}

/** One card: see the top of this file. */
export default function CompactCard({
  to, icon: Icon, name, figures = [], hover, loading = false, iconTone = 'plain',
}: CompactCardProps): ReactElement {
  const drawn = figures.slice(0, COMPACT_CARD_MAX_FIGURES);
  // Figures past the second are not drawn: the hover says all of them.
  const title = [hover, figures.length > COMPACT_CARD_MAX_FIGURES ? compactFiguresText(figures) : null]
    .filter(Boolean)
    .join('\n');
  return (
    <Link to={to} title={title || undefined} className={CARD}>
      <span className="flex min-w-0 items-center gap-2.5">
        <span aria-hidden="true" className={`${SQUARE} ${SQUARE_TONE[iconTone]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">{name}</span>
        <ArrowRight
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-primary-600"
        />
      </span>
      <span className={FIGURE_LINE}>
        {loading ? (
          <>
            <Strut />
            <Skeleton className="h-5 w-24 max-w-full self-center" />
          </>
        ) : drawn.length > 0 ? (
          <Figures figures={drawn} />
        ) : (
          <>
            <Strut />
            <span className={WORDS}>Open</span>
          </>
        )}
      </span>
    </Link>
  );
}
